import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './EmergencyFundGoalPage.module.css';
import LivesDisplay from '../components/LivesDisplay';
import { useQuest } from '../hooks/useQuest';
import { useLives } from '../context/LivesContext';
import { addXP } from '../utils/questUtils';

const EmergencyFundGoalPage: React.FC = () => {
  const navigate = useNavigate();
  const emergencyFundQuest = useQuest('emergency-fund');
  const { addLives } = useLives();
  // New state for monthly expenses and fund strength
  const [monthlyExpenses, setMonthlyExpenses] = useState('');
  const [fundStrength, setFundStrength] = useState<number | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Calculate the emergency fund goal
  const months = fundStrength || 0;
  const expenses = parseFloat(monthlyExpenses) || 0;
  const calculatedGoal = months > 0 && expenses > 0 ? (months * expenses) : 0;

  // Reset quest state when page loads to ensure it starts fresh
  useEffect(() => {
    console.log('[EmergencyFundGoalPage] Page loaded, resetting quest state');
    localStorage.removeItem('quest_emergency-fund');
    console.log('[EmergencyFundGoalPage] Quest state reset complete');
  }, []);

  const handleBack = () => {
    console.log('[EmergencyFundGoalPage] Back button clicked, navigating back without completing quest');
    navigate('/quests');
  };

  const handleSubmit = () => {
    if (calculatedGoal > 0) {
      console.log('[EmergencyFundGoalPage] Goal amount submitted:', calculatedGoal.toFixed(2));
      emergencyFundQuest.completeQuest(); // Mark as complete
      addLives(10); // Add 10 lives for completing the emergency fund quest
      addXP(50); // Award 50 XP for completing a quest
      localStorage.setItem('emergency_fund_goal_amount', calculatedGoal.toString());
      setShowSuccessModal(true);
    } else {
      alert('Please enter your monthly expenses and select a fund strength.');
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    navigate('/quests');
  };

  return (
    <div className={styles.emergencyFundPage}>
      <LivesDisplay />
      <h1 className={styles.title}>Build Your Emergency Fund</h1>
      <div className={styles.combinedSection}>
        <div className={styles.fundSection}>
          <h3 className={styles.sectionTitle}>Fund Strength</h3>
          <div className={styles.fundOptions}>
            <label className={styles.fundOption + (fundStrength === 3 ? ' ' + styles.selected : '')}>
              <input
                type="radio"
                name="fundStrength"
                value={3}
                checked={fundStrength === 3}
                onChange={() => setFundStrength(3)}
                style={{ marginRight: 8 }}
              />
              <strong>Basic (3 months)</strong>
            </label>
            <label className={styles.fundOption + (fundStrength === 6 ? ' ' + styles.selected : '')}>
              <input
                type="radio"
                name="fundStrength"
                value={6}
                checked={fundStrength === 6}
                onChange={() => setFundStrength(6)}
                style={{ marginRight: 8 }}
              />
              <strong>Standard (6 months)</strong>
            </label>
            <label className={styles.fundOption + (fundStrength === 12 ? ' ' + styles.selected : '')}>
              <input
                type="radio"
                name="fundStrength"
                value={12}
                checked={fundStrength === 12}
                onChange={() => setFundStrength(12)}
                style={{ marginRight: 8 }}
              />
              <strong>Fortified (12 months)</strong>
            </label>
          </div>
        </div>

        <div className={styles.expenseSection}>
          <h3 className={styles.sectionTitle}>Monthly Expenses</h3>
          <div className={styles.inputContainer}>
            <span className={styles.currencySymbol}>$</span>
            <input
              type="number"
              value={monthlyExpenses}
              onChange={(e) => setMonthlyExpenses(e.target.value)}
              placeholder="Total monthly essentials"
              className={styles.amountInput}
              min="0"
              step="0.01"
            />
          </div>
          <div className={styles.expenseList}>
            <span>Housing</span> • <span>Utilities</span> • <span>Groceries</span> • <span>Transportation</span> • <span>Debt minimums</span> • <span>Insurance & healthcare</span>
          </div>
        </div>

        <div className={styles.goalSection}>
          <h3 className={styles.sectionTitle}>Your Emergency Fund Goal</h3>
          <div className={styles.calculatedGoalBox}>
            {calculatedGoal > 0 ? (
              <>
                <div className={styles.goalAmount}>
                  <strong>${calculatedGoal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                </div>
              </>
            ) : (
              <span className={styles.placeholderText}>Select fund strength and enter expenses</span>
            )}
          </div>
        </div>
      </div>

      <div className={styles.goalInput}>
        <button 
          onClick={handleSubmit}
          className={styles.submitButton}
          disabled={calculatedGoal <= 0}
        >
          Set Goal
        </button>
      </div>
      <button className={styles.backButton} onClick={handleBack}>Back to Quests</button>

      {showSuccessModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.successModal}>
            <h2>Success!</h2>
            <p>Your emergency fund goal has been set.</p>
            <button className={styles.closeModalButton} onClick={handleCloseModal}>Back to Quests</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmergencyFundGoalPage; 